import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Button, Badge, getLeadTypeBadge } from '../../components/ui/Kit';
import EmptyState from '../../components/ui/EmptyState';
import Spinner from '../../components/ui/Spinner';
import Pagination from '../../components/ui/Pagination';
import SortableHeader from '../../components/ui/SortableHeader';
import CopyButton from '../../components/ui/CopyButton';
import LeadsFilters from './LeadsFilters';
import {
  EMPTY_LEADS_UI_FILTERS,
  buildLeadsApiFilters,
  countActiveLeadsFilters,
} from '../../lib/leadFilters';
import ImportCsvLeadsModal from './ImportCsvLeadsModal';
import ArchiveConfirmModal from '../../components/ui/ArchiveConfirmModal';
import DeleteConfirmModal from '../../components/ui/DeleteConfirmModal';
import BatchEditModal from '../../components/Leads/BatchEditModal';
import MergeLeadsModal from '../../components/Leads/MergeLeadsModal';
import { useToast } from '../../components/ui/Toast';
import Tooltip from '../../components/ui/Tooltip';
import { useSearch } from '../../contexts/SearchContext';
import {
  getActiveDirectoryUsers,
  resolveDisplayName,
  useUsersDirectory,
} from '../../lib/usersDirectory';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { formatDistanceToNow, format } from 'date-fns';

const {
  FiPlus, FiUsers, FiTarget, FiRefreshCw, FiUpload,
  FiMoreVertical, FiArchive, FiTrash2, FiRotateCcw, FiEdit2, FiGitMerge,
} = FiIcons;

const PAGE_SIZE = 25;
const SESSION_KEY = 'leads_nav_list';
const FILTERS_PREF_KEY = 'leads_list_filters_v1';
const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin']);

/** Resolve initial Agent filter: URL `agent` wins, then session preference, else current user (My Leads). */
function resolveInitialAgentFilter(userId, searchParams) {
  if (searchParams.has('agent')) {
    const raw = searchParams.get('agent');
    if (!raw || raw === 'all') return '';
    return raw;
  }
  try {
    const raw = sessionStorage.getItem(FILTERS_PREF_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'agent')) {
        return typeof parsed.agent === 'string' ? parsed.agent : '';
      }
    }
  } catch {
    /* ignore corrupt preference */
  }
  return userId || '';
}

function getStatusVariant(status) {
  const map = {
    new: 'primary', contacted: 'default', follow_up: 'warning',
    appointment_set: 'success', closed_won: 'success',
    closed_lost: 'default', dnc: 'danger',
  };
  return map[status] || 'default';
}

function buildAllContactText(lead) {
  return [
    `Name: ${lead.first_name} ${lead.last_name}`,
    lead.phone ? `Phone: ${lead.phone}` : null,
    lead.email ? `Email: ${lead.email}` : null,
    lead.notes ? `Notes: ${lead.notes}` : null,
  ].filter(Boolean).join('\n');
}

function ContactCell({ lead, navState }) {
  const allText = buildAllContactText(lead);
  return (
    <td className="px-6 py-4">
      <div className="flex items-center gap-1.5 mb-1">
        <Link
          to={`/leads/${lead.id}`}
          state={navState}
          className="font-medium text-primary hover:text-primary-dark hover:underline transition-colors"
        >
          {lead.first_name} {lead.last_name}
        </Link>
        <CopyButton text={allText} title="Copy all contact info" className="ml-0.5" />
      </div>
      {lead.phone && (
        <div className="flex items-center gap-1 text-gray-500 text-xs">
          <span>{lead.phone}</span>
          <CopyButton text={lead.phone} title="Copy phone" />
        </div>
      )}
      {lead.email && (
        <div className="flex items-center gap-1 text-gray-500 text-xs">
          <span className="truncate max-w-[160px]">{lead.email}</span>
          <CopyButton text={lead.email} title="Copy email" />
        </div>
      )}
      {!lead.phone && !lead.email && (
        <div className="text-gray-400 text-xs italic">No contact info</div>
      )}
    </td>
  );
}

function PeopleCell({ lead }) {
  const rows = [];
  const ownerName = resolveDisplayName(lead.created_by, lead.owner);
  if (ownerName) rows.push({ label: 'Owner', name: ownerName, isOwner: true });
  const assignedName = resolveDisplayName(lead.exclusive_to, lead.exclusive_holder);
  if (assignedName) rows.push({ label: 'Assigned', name: assignedName });
  const coWorkerName = resolveDisplayName(lead.co_worker_id, lead.co_worker);
  if (coWorkerName) rows.push({ label: 'Co-Worker', name: coWorkerName });

  if (!rows.length) {
    return <td className="px-6 py-4 text-gray-400 text-xs italic">—</td>;
  }

  return (
    <td className="px-6 py-4">
      <div className="space-y-0.5">
        {rows.map(({ label, name, isOwner }) => (
          <div key={label} className="text-xs leading-snug">
            <span className="text-gray-400">{label}: </span>
            <span className="text-gray-700 font-medium inline-flex items-center gap-1">
              {name}
              {isOwner && lead.previous_owner_name && (
                <Tooltip
                  content={`Previously owned by ${lead.previous_owner_name}${lead.previous_owner_email ? ` (${lead.previous_owner_email})` : ''}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-amber-100 text-amber-700 text-[9px] leading-none cursor-help"
                    aria-label="Previous owner"
                  >
                    ⟳
                  </span>
                </Tooltip>
              )}
            </span>
          </div>
        ))}
      </div>
    </td>
  );
}

function RowActionMenu({ lead, userRole, userId, onArchive, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const canDelete = MANAGER_ROLES.has(userRole);
  const canArchive =
    MANAGER_ROLES.has(userRole) ||
    lead.assignments?.some((a) => a.user_id === userId);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!canArchive && !canDelete) return null;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
      >
        <SafeIcon icon={FiMoreVertical} className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
          {canArchive && (
            <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => { setOpen(false); onArchive(lead); }}
            >
              <SafeIcon icon={FiArchive} className="w-4 h-4 text-amber-500" />
              Archive Lead
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              onClick={() => { setOpen(false); onDelete(lead); }}
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              Delete Lead
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function LeadsList() {
  // `rows` is the current page; `navIds` is every matching id, which drives both
  // the total count and the prev/next sequence the detail view walks.
  const [rows, setRows] = useState([]);
  const [navIds, setNavIds] = useState([]);
  const [otherViewCount, setOtherViewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [localSearch, setLocalSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [importLabels, setImportLabels] = useState([]);
  const [eventTitles, setEventTitles] = useState([]);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived'
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [archiveTarget, setArchiveTarget] = useState(null);
  const [unarchiveTarget, setUnarchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeLeadA, setMergeLeadA] = useState(null);
  const [mergeLeadB, setMergeLeadB] = useState(null);
  const [mergeLoading, setMergeLoading] = useState(false);
  const { users: directoryUsers } = useUsersDirectory();
  const agencyUsers = useMemo(
    () => getActiveDirectoryUsers(directoryUsers),
    [directoryUsers],
  );

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const { globalQuery } = useSearch();
  const { user } = useAuth();

  const isRecruits = location.pathname.includes('recruits');
  const userRole = user?.role || '';
  const userId = user?.id || '';
  const canDelete = MANAGER_ROLES.has(userRole);
  const filtersReady = Boolean(filters);

  // Default Agent filter to current user (My Leads). URL ?agent= / session pref override.
  useEffect(() => {
    if (!user?.id || filtersReady) return;
    setFilters({
      ...EMPTY_LEADS_UI_FILTERS,
      agent: resolveInitialAgentFilter(user.id, searchParams),
    });
  }, [user?.id, filtersReady, searchParams]);

  // Persist agent scope so an explicit "All Agents" choice survives soft navigation.
  useEffect(() => {
    if (!filtersReady) return;
    try {
      sessionStorage.setItem(FILTERS_PREF_KEY, JSON.stringify({ agent: filters.agent ?? '' }));
    } catch {
      /* ignore quota / private mode */
    }
  }, [filtersReady, filters?.agent]);

  // Debounce the search box so typing does not fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(localSearch), 300);
    return () => clearTimeout(t);
  }, [localSearch]);

  useEffect(() => {
    (async () => {
      try {
        const pips = await api.pipelines.getAll();
        const stages = new Set();
        for (const p of pips || []) {
          const list = Array.isArray(p.stages)
            ? p.stages
            : typeof p.stages === 'string'
              ? (() => {
                  try {
                    return JSON.parse(p.stages);
                  } catch {
                    return [];
                  }
                })()
              : [];
          list.forEach((s) => stages.add(s));
        }
        setPipelineStages([...stages]);
      } catch {
        setPipelineStages([]);
      }
    })();
  }, []);

  const loadImportFilterOptions = useCallback(async () => {
    try {
      const { importLabels: labels, eventTitles: events } = await api.leads.getImportFilterOptions();
      setImportLabels(labels || []);
      setEventTitles(events || []);
    } catch {
      setImportLabels([]);
      setEventTitles([]);
    }
  }, []);

  useEffect(() => {
    void loadImportFilterOptions();
  }, [loadImportFilterOptions]);

  useEffect(() => { setSelectedIds(new Set()); }, [viewMode]);

  const activeSearch = globalQuery || debouncedSearch;
  const archivedView = viewMode === 'archived';

  // Reset to page 1 during render rather than in an effect: the query below keys
  // off `page`, so an effect-based reset would fire one fetch for the stale page
  // and a second for page 1.
  const querySignature = JSON.stringify([filters, activeSearch, sortField, sortDir, viewMode]);
  const lastQuerySignature = useRef(querySignature);
  if (lastQuerySignature.current !== querySignature) {
    lastQuerySignature.current = querySignature;
    if (page !== 1) setPage(1);
  }

  const loadLeads = useCallback(async () => {
    if (!filters || !user?.id) return;
    setLoading(true);
    try {
      const apiFilters = buildLeadsApiFilters(filters, { isRecruits });
      const queryOptions = {
        agentId: filters.agent || null,
        search: activeSearch,
        sortField,
        sortDir,
      };
      const [pageResult, otherCount] = await Promise.all([
        api.leads.getPage(apiFilters, {
          ...queryOptions,
          page,
          pageSize: PAGE_SIZE,
          archived: archivedView,
        }),
        // The tab we are not looking at still shows a count.
        api.leads.getCount(apiFilters, { ...queryOptions, archived: !archivedView }),
      ]);
      setRows(pageResult.rows);
      setNavIds(pageResult.ids);
      setOtherViewCount(otherCount);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load leads', message: err.message });
      setRows([]);
      setNavIds([]);
      setOtherViewCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters, user?.id, isRecruits, activeSearch, sortField, sortDir, page, archivedView, addToast]);

  useEffect(() => {
    if (!user?.id || !filtersReady) return;
    void loadLeads();
  }, [loadLeads, user?.id, filtersReady]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Own-leads default (agent === current user) is not counted as an "active" filter.
  const activeFilterCount = filters
    ? countActiveLeadsFilters({
        ...filters,
        agent: filters.agent && filters.agent !== userId ? filters.agent : '',
      }) + (localSearch ? 1 : 0)
    : 0;

  const clearAllFilters = () => {
    setLocalSearch('');
    setFilters({ ...EMPTY_LEADS_UI_FILTERS, agent: userId || '' });
  };

  const showingOwnLeads = Boolean(filters?.agent && filters.agent === userId);

  const totalItems = navIds.length;
  const activeCount = archivedView ? otherViewCount : totalItems;
  const archivedCount = archivedView ? totalItems : otherViewCount;

  useEffect(() => {
    if (!loading && navIds.length > 0) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(navIds));
    }
  }, [navIds, loading]);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const sortProps = { sortField, sortDir, onSort: handleSort };

  // --- Selection helpers ---
  const allPageSelected = rows.length > 0 && rows.every((l) => selectedIds.has(l.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        rows.forEach((l) => next.delete(l.id));
      } else {
        rows.forEach((l) => next.add(l.id));
      }
      return next;
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // --- Archive / Unarchive / Delete handlers ---
  const handleArchiveConfirm = async () => {
    const target = archiveTarget;
    setArchiveTarget(null);
    if (!target) return;
    try {
      await api.leads.archive(target.id);
      addToast({ type: 'success', title: 'Lead archived' });
      await loadLeads();
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to archive', message: err.message });
    }
  };

  const handleUnarchiveConfirm = async () => {
    const target = unarchiveTarget;
    setUnarchiveTarget(null);
    if (!target) return;
    try {
      await api.leads.unarchive(target.id);
      addToast({ type: 'success', title: 'Lead unarchived' });
      await loadLeads();
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to unarchive', message: err.message });
    }
  };

  const handleDeleteConfirm = async () => {
    const target = deleteTarget;
    setIsDeleting(true);
    try {
      await api.leads.delete(target.id);
      setDeleteTarget(null);
      addToast({ type: 'success', title: 'Lead permanently deleted' });
      await loadLeads();
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to delete', message: err.message });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;
    setBulkArchiving(true);
    try {
      await Promise.all([...selectedIds].map((lid) => api.leads.archive(lid)));
      addToast({ type: 'success', title: `${selectedIds.size} lead(s) archived` });
      setSelectedIds(new Set());
      await loadLeads();
    } catch (err) {
      addToast({ type: 'error', title: 'Bulk archive failed', message: err.message });
    } finally {
      setBulkArchiving(false);
    }
  };

  const handleBulkMergeClick = async () => {
    const ids = [...selectedIds];
    if (ids.length !== 2) return;
    setMergeLoading(true);
    try {
      const [a, b] = await Promise.all(ids.map((lid) => api.leads.getById(lid)));
      setMergeLeadA(a);
      setMergeLeadB(b);
      setMergeModalOpen(true);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Could not load leads for merge',
        message: err.message,
      });
    } finally {
      setMergeLoading(false);
    }
  };

  const handleMergeComplete = (survivingLead) => {
    setMergeModalOpen(false);
    setMergeLeadA(null);
    setMergeLeadB(null);
    setSelectedIds(new Set());
    void loadLeads();
    if (survivingLead?.id) {
      navigate(`/leads/${survivingLead.id}`);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedIds].map((lid) => api.leads.delete(lid)));
      setBulkDeleteModalOpen(false);
      addToast({ type: 'success', title: `${selectedIds.size} lead(s) permanently deleted` });
      setSelectedIds(new Set());
      await loadLeads();
    } catch (err) {
      addToast({ type: 'error', title: 'Bulk delete failed', message: err.message });
    } finally {
      setBulkDeleting(false);
    }
  };

  const selectedCount = selectedIds.size;
  const bulkSelectedName = selectedCount === 1
    ? (() => { const l = rows.find((x) => selectedIds.has(x.id)); return l ? `${l.first_name} ${l.last_name}` : '1 lead'; })()
    : `${selectedCount} leads`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isRecruits
              ? 'Recruits'
              : showingOwnLeads
                ? 'My Leads'
                : 'All Leads'}
          </h1>
          <p className="text-sm text-gray-500">
            Manage and track your {isRecruits ? 'potential agents' : 'insurance leads'}.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={loadLeads} disabled={loading}>
            {loading ? <Spinner /> : <SafeIcon icon={FiRefreshCw} className="h-4 w-4" />}
          </Button>
          <Button variant="outline" onClick={() => setImportCsvOpen(true)}>
            <SafeIcon icon={FiUpload} className="mr-2" />
            Import CSV
          </Button>
          <Link to="/leads/new" state={{ lead_type: isRecruits ? 'recruit' : 'fresh' }}>
            <Button>
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add {isRecruits ? 'Recruit' : 'Lead'}
            </Button>
          </Link>
        </div>
      </div>

      {/* Active / Archived Toggle */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          type="button"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            viewMode === 'active'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('active')}
        >
          Active ({activeCount})
        </button>
        <button
          type="button"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
            viewMode === 'archived'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('archived')}
        >
          <SafeIcon icon={FiArchive} className="w-3.5 h-3.5" />
          Archived ({archivedCount})
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-blue-800">
            {selectedCount} selected
          </span>
          <div className="h-4 w-px bg-blue-200" />
          {viewMode === 'active' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkArchive}
              disabled={bulkArchiving}
            >
              {bulkArchiving ? <Spinner className="mr-1" /> : <SafeIcon icon={FiArchive} className="mr-1 w-3.5 h-3.5" />}
              Archive Selected
            </Button>
          )}
          {viewMode === 'active' && selectedCount === 2 && (
            <button
              type="button"
              onClick={handleBulkMergeClick}
              disabled={mergeLoading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#00AAFF] text-[#00AAFF] text-sm font-medium hover:bg-[#00AAFF]/10 transition-colors disabled:opacity-50"
            >
              {mergeLoading ? (
                <Spinner className="w-3.5 h-3.5" />
              ) : (
                <SafeIcon icon={FiGitMerge} size={15} />
              )}
              Merge
            </button>
          )}
          {(user?.role === 'admin' || user?.role === 'super_admin') && (
            <button
              type="button"
              onClick={() => setShowBatchEditModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#00AAFF] text-[#00AAFF] text-sm font-medium hover:bg-[#00AAFF]/10 transition-colors"
            >
              <SafeIcon icon={FiEdit2} size={15} />
              Batch Edit
            </button>
          )}
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => setBulkDeleteModalOpen(true)}
              disabled={bulkDeleting}
            >
              <SafeIcon icon={FiTrash2} className="mr-1 w-3.5 h-3.5" />
              Delete Selected
            </Button>
          )}
          <button
            type="button"
            className="ml-auto text-xs text-blue-600 hover:text-blue-800"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </button>
        </div>
      )}

      <Card className="overflow-hidden">
        {filters ? (
          <LeadsFilters
            localSearch={localSearch}
            setLocalSearch={setLocalSearch}
            filters={filters}
            setFilters={setFilters}
            agents={agencyUsers}
            pipelineStages={pipelineStages}
            importLabels={importLabels}
            eventTitles={eventTitles}
            isRecruits={isRecruits}
            activeFilterCount={activeFilterCount}
            onClearAll={clearAllFilters}
            showAdvanced={showAdvanced}
            setShowAdvanced={setShowAdvanced}
            currentUserId={userId}
          />
        ) : (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm text-gray-500">
            Loading filters…
          </div>
        )}
        {globalQuery && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700">
            Showing global search results for <span className="font-semibold">"{globalQuery}"</span>
          </div>
        )}

        {activeFilterCount > 0 && !globalQuery && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700 flex items-center gap-2">
            <span>
              Showing <span className="font-semibold">{totalItems}</span> {isRecruits ? 'recruits' : 'leads'} matching active filters
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Spinner size="lg" className="text-primary" />
            <p className="text-sm text-gray-500">Loading {isRecruits ? 'recruits' : 'leads'}…</p>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={viewMode === 'archived' ? FiArchive : (isRecruits ? FiTarget : FiUsers)}
            title={
              viewMode === 'archived'
                ? 'No archived leads'
                : (activeSearch || activeFilterCount > 0 ? 'No results found' : isRecruits ? 'No recruits yet' : 'No leads yet')
            }
            description={
              viewMode === 'archived'
                ? 'Archived leads will appear here.'
                : (activeSearch || activeFilterCount > 0
                  ? 'Try adjusting your search or filters.'
                  : isRecruits
                  ? 'Start building your agent pipeline by adding your first recruit.'
                  : 'Add your first lead to get started.')
            }
            actionLabel={
              viewMode === 'archived'
                ? null
                : (activeSearch || activeFilterCount > 0 ? 'Clear Filters' : `Add ${isRecruits ? 'Recruit' : 'Lead'}`)
            }
            onAction={
              viewMode === 'archived'
                ? undefined
                : (activeSearch || activeFilterCount > 0
                  ? clearAllFilters
                  : () => navigate('/leads/new', { state: { lead_type: isRecruits ? 'recruit' : 'fresh' } }))
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="pl-4 pr-2 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </th>
                    <SortableHeader label="Name & Contact" field="name" {...sortProps} className="px-6 py-3" />
                    <SortableHeader label="Type" field="lead_type" {...sortProps} className="px-6 py-3" />
                    <SortableHeader label="Status" field="status" {...sortProps} className="px-6 py-3" />
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">People</th>
                    {viewMode === 'archived' ? (
                      <SortableHeader label="Archived on" field="archived_at" {...sortProps} className="px-6 py-3" />
                    ) : (
                      <SortableHeader label="Added" field="created_at" {...sortProps} className="px-6 py-3" />
                    )}
                    <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((lead, pageIdx) => {
                    const globalIdx = (page - 1) * PAGE_SIZE + pageIdx;
                    const navState = { leadIds: navIds, currentIndex: globalIdx };
                    return (
                      <tr
                        key={lead.id}
                        className={`border-b hover:bg-gray-50 transition-colors ${
                          selectedIds.has(lead.id) ? 'bg-blue-50/50' : 'bg-white'
                        }`}
                      >
                        <td className="pl-4 pr-2 py-4 w-10">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(lead.id)}
                            onChange={() => toggleSelect(lead.id)}
                            className="rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </td>
                        <ContactCell lead={lead} navState={navState} />
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {getLeadTypeBadge(lead.lead_type)}
                            {lead.exclusivity_status === 'non_exclusive' && (
                              <Badge variant="success" className="text-[10px] px-1.5 py-0">
                                Open
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant={getStatusVariant(lead.status)}>
                            {lead.status?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <PeopleCell lead={lead} />
                        <td className="px-6 py-4 text-gray-500 text-xs">
                          {viewMode === 'archived' && lead.archived_at
                            ? format(new Date(lead.archived_at), 'MMM d, yyyy')
                            : formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {viewMode === 'archived' ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setUnarchiveTarget(lead)}
                                  title="Unarchive"
                                >
                                  <SafeIcon icon={FiRotateCcw} className="w-3.5 h-3.5 mr-1" />
                                  Unarchive
                                </Button>
                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => setDeleteTarget(lead)}
                                    title="Delete permanently"
                                  >
                                    <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </>
                            ) : (
                              <>
                                <Link to={`/leads/${lead.id}`} state={navState}>
                                  <Button variant="ghost" size="sm">View</Button>
                                </Link>
                                <RowActionMenu
                                  lead={lead}
                                  userRole={userRole}
                                  userId={userId}
                                  onArchive={(l) => setArchiveTarget(l)}
                                  onDelete={(l) => setDeleteTarget(l)}
                                />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <ImportCsvLeadsModal
        isOpen={importCsvOpen}
        onClose={() => setImportCsvOpen(false)}
        isRecruits={isRecruits}
        onImported={() => {
          loadLeads();
          void loadImportFilterOptions();
        }}
      />

      {/* Archive confirm */}
      <ArchiveConfirmModal
        isOpen={!!archiveTarget}
        onConfirm={handleArchiveConfirm}
        onCancel={() => setArchiveTarget(null)}
        leadName={archiveTarget ? `${archiveTarget.first_name} ${archiveTarget.last_name}` : ''}
      />

      {/* Unarchive confirm */}
      <ArchiveConfirmModal
        isOpen={!!unarchiveTarget}
        onConfirm={handleUnarchiveConfirm}
        onCancel={() => setUnarchiveTarget(null)}
        leadName={unarchiveTarget ? `${unarchiveTarget.first_name} ${unarchiveTarget.last_name}` : ''}
        isUnarchive
      />

      {/* Single delete confirm */}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        leadName={deleteTarget ? `${deleteTarget.first_name} ${deleteTarget.last_name}` : ''}
        isDeleting={isDeleting}
      />

      {/* Bulk delete confirm */}
      <DeleteConfirmModal
        isOpen={bulkDeleteModalOpen}
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteModalOpen(false)}
        leadName={bulkSelectedName}
        isDeleting={bulkDeleting}
      />

      {showBatchEditModal && (
        <BatchEditModal
          selectedIds={[...selectedIds]}
          agencyId={user?.agency_id}
          onClose={() => setShowBatchEditModal(false)}
          onSuccess={() => {
            setShowBatchEditModal(false);
            void loadLeads();
          }}
        />
      )}

      <MergeLeadsModal
        isOpen={mergeModalOpen}
        onClose={() => {
          setMergeModalOpen(false);
          setMergeLeadA(null);
          setMergeLeadB(null);
        }}
        leadA={mergeLeadA}
        leadB={mergeLeadB}
        agencyUsers={[]}
        onMerged={handleMergeComplete}
      />
    </div>
  );
}
